import React, { useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import SingleItemPage from "./SingleItemPage";
import "../../../assets/styles/component/customer/itemDetail/ProductImageGallery.scss";
import VariantProductPage from "./VariantProductPage";

function ImageGalleryMobile(props) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const extractOverview = (htmlText) => {
    if (!htmlText) return "Enhances natural wood grain. Fast drying formula for quick results.";
    return htmlText;
  };

  const extractFeatures = (htmlText) => {
    if (!htmlText) return {
      type: "html",
      content: `
        <ul>
          <li>Premium quality formulation</li>
          <li>Durable and long-lasting finish</li>
          <li>Easy to apply with smooth results</li>
        </ul>
      `
    };
    
    return {
      type: "html",
      content: htmlText
    };
  };

  const getBannerData = () => {
    const availableImages = props.images || [];
    const imageCount = availableImages.length;
    const title = props.data?.order_title || props.order_title || "Varathane";
    const brand = props.data?.brand_name || props.order_brand_name || "PREMIUM FAST DRY WOOD STAIN";
    
    // Get the currently active variant or use the first variant if none selected
    const variants = props.data?.variants || [];
    const activeVariant = variants.find(v => v.id === props.data?.variantId) || variants[0];
    
    const sku = activeVariant?.sku || props.data?.sku || "SKU12345";
    const price = activeVariant?.discounted_price || activeVariant?.price || 
                 props.data?.price_after_discount ? 
                 `${props.data.currency} ${props.data.price_after_discount}` : 
                 "$24.99";

    const overviewText = extractOverview(props.data?.[`description_${props.language}`]);
    const featuresData = extractFeatures(props.data?.[`features_${props.language}`]);

    const secondBannerDescription = featuresData.content;

    const variantInfo = activeVariant ? (
      <div className="simple-variant-info">
        <div className="variant-name">
          {props.language === 'ar' ? 
            activeVariant.variation_ar || activeVariant.variation : 
            activeVariant.variation}
        </div>
        <div className="variant-price">
          {props.data.currency} {activeVariant.discounted_price || activeVariant.price}
        </div>
        <div className="variant-sku">SKU: {activeVariant.sku}</div>
      </div>
    ) : null;

    return [
      {
        title: brand,
        subtitle: title,
        description: "",
        bgColor: "#fff",
        accentPosition: "bottom-right",
        imagePos: "left",
        textPos: "right",
        textColor: "#333",
        transparentImage: true,
        className: "first-banner"
      },
      {
        title: "Features",
        subtitle: "",
        description: secondBannerDescription,
        featuresData: featuresData,
        bgColor: "#fff",
        accentPosition: availableImages[1] ? "bottom-left-large" : "full-brown",
        imagePos: "",
        textPos: "left",
        textColor: availableImages[1] ? "#fff" : "#fff",
        transparentImage: true,
        bannerClass: availableImages[1] ? "second-banner-with-image" : "second-banner-no-image",
        className: "second-banner",
        useDescriptionForFeatures: true
      },
      {
        title: "",
        subtitle: "",
        description: overviewText,
        bgColor: "#fff",
        accentPosition: availableImages[2] ? "right-full-brown" : "full-brown",
        imagePos: "",
        textPos: "right",
        textColor: "#fff",
        transparentImage: false,
        className: imageCount === 3 ? "third-banner-single" : "third-banner",
        isParagraph: true
      },
      {
        title: 'Details',
        subtitle: "",
        description: "",
        variantInfo: variantInfo,
        bgColor: "#fff",
        accentPosition: "top-left-bottom-right",
        imagePos: "right-contained",
        textPos: "left",
        textColor: "#333",
        transparentImage: true,
        className: "fourth-banner",
        hasVariants: variants.length > 0
      }
    ].map((banner, index) => ({
      ...banner,
      image: availableImages[index] || null
    }));
  };

  const handlePrev = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    const allImages = [...(props.images?.slice(4) || []).filter(img => img && img.img), 
                  ...(props.images?.slice(0, 4) || []).filter(img => img && img.img)];
    setCurrentIndex(prev => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  const handleNext = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    const allImages = [...(props.images?.slice(4) || []).filter(img => img && img.img), 
                  ...(props.images?.slice(0, 4) || []).filter(img => img && img.img)];
    setCurrentIndex(prev => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  const renderImageSlider = () => {
    const allImages = [...(props.images?.slice(4) || []).filter(img => img && img.img), 
                  ...(props.images?.slice(0, 4) || []).filter(img => img && img.img)];
    const totalImages = allImages.length;
    
    if (totalImages === 0) return null;

    const getImageIndex = (offset) => {
      let index = currentIndex + offset;
      if (index < 0) index += totalImages;
      if (index >= totalImages) index -= totalImages;
      return index;
    };

    const visibleIndices = Array.from({ length: Math.min(6, totalImages) }, (_, i) => getImageIndex(i));

    return (
      <div className="additional-images-section">
        {totalImages > 6 && (
          <>
            <button 
              className="slider-arrow left-arrow" 
              onClick={handlePrev}
              aria-label="Previous images"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
              </svg>
            </button>
            <button 
              className="slider-arrow right-arrow" 
              onClick={handleNext}
              aria-label="Next images"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
              </svg>
            </button>
          </>
        )}
        <div 
          className={`additional-images-container ${isAnimating ? 'animating' : ''}`}
        >
          {visibleIndices.map((index) => {
            const image = allImages[index];
            if (!image || !image.img) return null;
            
            return (
              <div 
                key={`additional-${index}`} 
                className="additional-image-wrapper"
              >
                <img
                  src={image.img}
                  alt={image.alt || `Product image ${index + 1}`}
                  className="additional-image"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const banners = getBannerData();
  const imageCount = props.images?.length || 0;
  const remainingImages = imageCount > 4 ? props.images.slice(4) : [];

  if (imageCount <= 1) {
    return (
      <div className="image"> 
      <div className="image-gallery-mobile">
        <div>
          <SingleItemPage 
            images={props.images}
            data={props.data}
          />
        </div>
        <div>
         <VariantProductPage 
          images={props.images}
          data={props.data}
        />
        </div>
        
        
      </div>
      {remainingImages.length > 0 && renderImageSlider()}
      </div>
    );
  }

  return (
    <div className="image"> 
    <div className="image-gallery-mobile">
      <h6 className={"heading " + (props.language === "en" ? "text-left" : "text-right")}>
        {t("item-detail.product-image-gallery")}
      </h6>

      <div>
        {imageCount === 2 && (
          <div className="banners-row">
            {banners.slice(0, 2).map((banner, index) => (
              <Banner key={`banner-${index}`} {...banner} />
            ))}
          </div>
        )}

        {imageCount === 3 && (
          <>
            <div className="banners-row">
              {banners.slice(0, 2).map((banner, index) => (
                <Banner key={`banner-${index}`} {...banner} />
              ))}
            </div>
            <div className="single-banner-center">
              <Banner key="banner-2" {...banners[2]} />
            </div>
          </>
        )}

        {imageCount >= 4 && (
          <>
            <div className="banners-row">
              {banners.slice(0, 2).map((banner, index) => (
                <Banner key={`banner-${index}`} {...banner} />
              ))}
            </div>
            <div className="banners-row">
              {banners.slice(2, 4).map((banner, index) => (
                <Banner key={`banner-${index + 2}`} {...banner} />
              ))}
            </div>
          </>
        )}
      </div>
      
      <div>
        <SingleItemPage 
          images={props.images}
          data={props.data}
        />
      </div>
      
      <div>
        <VariantProductPage 
          images={props.images}
          data={props.data}
        />
      </div>
      
 
      
    </div>
       {remainingImages.length > 0 && renderImageSlider()}
    </div>
  );
}

const Banner = ({
  title,
  subtitle,
  description,
  featuresData,
  variantInfo,
  accentPosition,
  image,
  imagePos,
  textPos,
  textColor,
  transparentImage,
  price,
  className,
  bannerClass,
  isParagraph,
  useDescriptionForFeatures,
  hasVariants
}) => {
  const bannerClasses = `banner-container ${bannerClass || ''}`;
  const textClasses = `banner-text-content ${className || ''}`;
  const imageClasses = `banner-image-container ${className || ''} ${transparentImage ? 'transparent-bg' : 'white-bg'}`;
  const imageStyleClass = `banner-image ${className || ''}`;

  const renderFeaturesContent = () => {
    if (!featuresData) return null;

    if (featuresData.type === "html") {
      return (
        <div 
          className={`features-content-container ${className || ''}`}
          dangerouslySetInnerHTML={{ __html: featuresData.content }}
        />
      );
    }

    return null;
  };

  return (
    <div className={bannerClasses} style={{ flexDirection: textPos === "left" ? "row" : "row-reverse" }}>
      {accentPosition === "full-brown" && <div className="accent-full-brown"></div>}
      {accentPosition === "bottom-left-large" && <div className="accent-bottom-left-large"></div>}
      {accentPosition === "bottom-right" && <div className="accent-bottom-right"></div>}
      {accentPosition === "right-full-brown" && <div className="accent-right-full-brown"></div>}
      {accentPosition === "top-left-bottom-right" && (
        <>
          <div className="accent-top-left"></div>
          <div className="accent-bottom-right-angle"></div>
        </>
      )}

      <div className={textClasses} style={{ 
        color: textColor,
        fontSize: isParagraph ? '16px' : undefined
      }}>
        {title && <h1 className={`banner-title ${className || ''}`}>{title}</h1>}
        {hasVariants && variantInfo}
        {subtitle && <h2 className={`banner-subtitle ${className || ''}`}>{subtitle}</h2>}
        {price && <p className={`banner-price ${bannerClass || ''}`}>{price}</p>}
        
        {useDescriptionForFeatures ? (
          <>
            {renderFeaturesContent()}
          </>
        ) : isParagraph ? (
          <div 
            className={`banner-description  ${className || ''}`}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        ) : (
          <div className={`banner-description  ${className || ''}`}>
            {description}
          </div>
        )}
      </div>

      {image && (
        <div className={imageClasses}>
          <img
            src={image.img}
            alt={image.alt || "Product image"}
            className={imageStyleClass}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  width: state.layout.screenWidth,
  language: state.language.language,
  data: state.item.item,
});

export default connect(mapStateToProps, null)(ImageGalleryMobile);